import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, Play, Clock, Search, FileOutput } from 'lucide-react';
import { useRef } from 'react';
import { track } from '@/lib/analytics/mixpanel';

const BENEFIT_TILES = [
  { icon: Search, label: 'Multiple file formats' },
  { icon: Clock, label: 'No credit card' },
  { icon: FileOutput, label: 'Free to start' },
] as const;

export function HeroSection() {
  const navigate = useNavigate();
  const sectionRef = useRef<HTMLElement>(null);

  const scrollToDemo = () => {
    track('hero_try_demo_click');
    document.getElementById('interactive-demo')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleGetStarted = () => {
    track('hero_get_started_click');
    navigate('/auth');
  };

  return (
    <section
      ref={sectionRef}
      className="relative min-h-[70svh] lg:min-h-svh flex flex-col items-center justify-center bg-[hsl(225,25%,16%)]"
      aria-labelledby="hero-heading"
    >
      {/* Radial vignette for subtle depth */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse at 50% 0%, hsl(230 30% 20% / 0.5), transparent 70%)' }}
        aria-hidden="true"
      />

      {/* Bottom hard edge — clean transition */}
      <div
        className="absolute bottom-0 left-0 right-0 h-px bg-border/20"
        aria-hidden="true"
      />

      <div className="relative max-w-3xl 2xl:max-w-4xl mx-auto px-5 sm:px-6 2xl:px-8 pt-20 sm:pt-24 md:pt-28 lg:pt-32 2xl:pt-36 pb-12 md:pb-16 lg:pb-20 2xl:pb-24 text-center">

        {/* Eyebrow */}
        <p className="mb-5 lg:mb-6 text-[11px] 2xl:text-xs font-medium uppercase tracking-[0.2em] text-white/50 animate-hero-fade-up">
          AI Qualitative Research Platform
        </p>

        {/* Headline */}
        <h1
          id="hero-heading"
          className="text-[2.75rem] sm:text-6xl md:text-7xl 2xl:text-8xl font-bold tracking-tight leading-[1.05] text-white animate-hero-fade-up"
        >
          Press <span className="text-amber-400">submit<span className="animate-blink-period">.</span></span>
        </h1>

        {/* Subhead */}
        <p
          className="mt-5 sm:mt-7 lg:mt-8 text-base sm:text-lg 2xl:text-xl text-white/60 leading-relaxed mx-auto animate-hero-fade-up [animation-delay:100ms]"
          style={{ maxWidth: '56ch' }}
        >
          Turn raw qualitative data into submission-ready findings.
        </p>

        {/* CTAs */}
        <div
          className="mt-7 sm:mt-9 lg:mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 animate-hero-fade-up [animation-delay:200ms]"
        >
          <Button
            size="lg"
            onClick={handleGetStarted}
            className="w-full sm:w-auto min-w-[180px] h-12 sm:h-13 2xl:h-14 text-sm sm:text-base 2xl:text-lg font-medium gradient-primary shadow-lg hover:shadow-xl hover:shadow-primary/20 transition-all duration-300 group"
            aria-label="Start free: sign up for Verithos"
          >
            Start Free
            <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-0.5 transition-transform" aria-hidden="true" />
          </Button>

          <button
            onClick={scrollToDemo}
            className="inline-flex items-center gap-2 text-sm font-medium text-white/50 hover:text-white/80 transition-colors"
            aria-label="Watch how it works: try the interactive demo"
          >
            <Play className="h-3.5 w-3.5" aria-hidden="true" />
            Watch how it works
          </button>
        </div>

        {/* Benefit tiles */}
        <div className="mt-7 lg:mt-9 grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-3.5 max-w-xs sm:max-w-2xl 2xl:max-w-3xl mx-auto animate-hero-fade-up [animation-delay:300ms]">
          {BENEFIT_TILES.map(({ icon: Icon, label }) => (
            <div
              key={label}
              className="flex items-center gap-2.5 sm:justify-center px-4 py-2.5 rounded-lg border border-white/10 bg-white/[0.04]"
            >
              <Icon className="h-4 w-4 shrink-0 text-white/40" aria-hidden="true" />
              <span className="text-[13px] font-medium text-white/70 whitespace-nowrap">{label}</span>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}
